/**
 * Spend and Earn Progress Bar
 *
 * Supports N independent rewards (free shipping + free gifts) on a single
 * progress bar. Starts in single-scale mode (0 → first reward), then reveals
 * full scale with animated transition when first reward is reached.
 *
 * Configuration is passed via data-rewards JSON attribute from Liquid.
 *
 * @class SpendAndEarn
 * @extends HTMLElement
 */

if (!customElements.get('spend-and-earn')) {
  class SpendAndEarn extends HTMLElement {
    static VERSION = 4;
    static STORAGE_KEY = `spend-and-earn-state-v${SpendAndEarn.VERSION}`;
    static DEBUG = false;
    static ENABLE_HAPTICS = true;

    // Shared across instances — intentionally static so the flag survives
    // component recreation during section re-rendering.
    static isCartUpdating = false;
    static cartUpdateTimestamp = 0;

    static STATES = {
      IDLE: 'idle',
      ANIMATING: 'animating',
      CELEBRATING: 'celebrating',
      TRANSITIONING: 'transitioning'
    };

    static TRANSITIONS = {
      idle: ['animating', 'celebrating', 'transitioning'],
      animating: ['idle', 'celebrating', 'transitioning'],
      celebrating: ['idle', 'transitioning'],
      transitioning: ['idle', 'animating']
    };

    static SCALE_MODES = {
      SINGLE: 'single',
      FULL: 'full'
    };

    static TIMING = {
      FILL_DURATION: 500,
      FILL_DURATION_SLOW: 1200,
      MARKER_CELEBRATE: 500,
      CELEBRATION_PAUSE: 600,
      CHECK_POP: 350,
      CHECK_HOLD: 400,
      LOCKED_EXIT: 800,
      LOCKED_EXIT_START_DELAY: 200,
      NEXT_LOCKED_ENTER_DELAY: 300,
      NEXT_LOCKED_ENTER_DURATION: 900,
      UNLOCKED_ENTER: 800,
      SCALE_TRANSITION: 500,
      SCALE_DELAY: 600,
      EASING: 'cubic-bezier(0.4, 0, 0.2, 1)',
      EASE_OUT_SLOW: 'cubic-bezier(0.16, 1, 0.3, 1)'
    };

    static APPROACH_THRESHOLD = 0.85;

    constructor() {
      super();

      this.state = SpendAndEarn.STATES.IDLE;
      this.scaleMode = SpendAndEarn.SCALE_MODES.SINGLE;
      this.animationAbortController = null;
      this.debounceTimer = null;
      this.timeouts = [];

      this.elements = {};
      this.rewards = [];
      this.maxThreshold = 0;
      this.firstRewardPosition = 100;

      this.handleCartUpdateStart = this.handleCartUpdateStart.bind(this);
      this.handleCartUpdate = this.onCartUpdate.bind(this);
      this.handleCartUpdateError = this.handleCartUpdateError.bind(this);
    }

    // ──────────────────────────────────────────────
    // Lifecycle
    // ──────────────────────────────────────────────

    connectedCallback() {
      this.loadConfig();
      this.cacheElements();
      this.initialize();
      this.attachEventListeners();
      this.attachMicroInteractions();
    }

    disconnectedCallback() {
      this.detachEventListeners();
      this.cancelAnimations();
      this.clearAllTimeouts();
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
    }

    // ──────────────────────────────────────────────
    // Configuration
    // ──────────────────────────────────────────────

    loadConfig() {
      this.rewards = JSON.parse(this.dataset.rewards || '[]')
        .sort((a, b) => a.threshold - b.threshold);

      this.maxThreshold = this.rewards.length > 0
        ? this.rewards[this.rewards.length - 1].threshold
        : 0;

      this.firstRewardPosition = this.rewards.length > 1
        ? (this.rewards[0].threshold / this.maxThreshold) * 100
        : 100;
    }

    cacheElements() {
      this.elements = {
        message: this.querySelector('[data-message]'),
        track: this.querySelector('[data-track]'),
        fill: this.querySelector('[data-fill]'),
        particles: this.querySelector('[data-particles]'),
        confetti: this.querySelector('[data-confetti]'),
        nextGift: this.querySelector('[data-next-gift]'),
        markers: [],
        nextGiftItems: [],
        rewardItems: []
      };

      // Match DOM elements to sorted rewards by type + threshold
      this.rewards.forEach(r => {
        const sel = `[data-reward-type="${r.type}"][data-reward-threshold="${r.threshold}"]`;
        this.elements.markers.push(this.querySelector(`[data-reward-marker]${sel}`));
        this.elements.nextGiftItems.push(this.querySelector(`[data-next-gift-item]${sel}`));
        this.elements.rewardItems.push(this.querySelector(`[data-reward-item]${sel}`));
      });

      // Mark the highest-threshold marker for CSS sizing
      const lastMarker = this.elements.markers[this.rewards.length - 1];
      if (lastMarker) lastMarker.dataset.finalMarker = '';
    }

    // ──────────────────────────────────────────────
    // State management
    // ──────────────────────────────────────────────

    canTransitionTo(newState) {
      return SpendAndEarn.TRANSITIONS[this.state]?.includes(newState) ?? false;
    }

    transitionTo(newState) {
      if (!this.canTransitionTo(newState)) {
        this.warn(`Invalid state transition ${this.state} -> ${newState}`);
        return false;
      }
      this.state = newState;
      return true;
    }

    setScaleMode(mode) {
      this.scaleMode = mode;
      this.dataset.scaleMode = mode;
    }

    loadSessionState() {
      try {
        const stored = sessionStorage.getItem(SpendAndEarn.STORAGE_KEY);
        return stored ? JSON.parse(stored) : null;
      } catch {
        return null;
      }
    }

    saveSessionState(total, rewardIndex) {
      try {
        sessionStorage.setItem(SpendAndEarn.STORAGE_KEY, JSON.stringify({
          total,
          rewardIndex,
          scaleMode: this.scaleMode,
          timestamp: Date.now()
        }));
      } catch {
        // Ignore storage errors
      }
    }

    // ──────────────────────────────────────────────
    // Core calculations
    // ──────────────────────────────────────────────

    /** Returns count of unlocked rewards (0 to rewards.length) */
    getRewardIndex(total) {
      let index = 0;
      for (const reward of this.rewards) {
        if (total >= reward.threshold) index++;
        else break;
      }
      return index;
    }

    calculateProgress(total) {
      if (this.rewards.length === 0) return 0;

      const threshold = this.scaleMode === SpendAndEarn.SCALE_MODES.SINGLE
        ? this.rewards[0].threshold
        : this.maxThreshold;

      return Math.min(100, Math.max(0, (total / threshold) * 100));
    }

    // ──────────────────────────────────────────────
    // UI updates
    // ──────────────────────────────────────────────

    updateUI(total) {
      this.updateMessage(total);
      this.updateMarkers(total);
      this.updateNextGiftPreview(total);
      this.updateRewardsList(total);
    }

    updateMessage(total, animate = true) {
      if (!this.elements.message || this.rewards.length === 0) return;

      const format = window.theme?.settings?.money_with_currency_format || '${{amount}}';
      const rewardIndex = this.getRewardIndex(total);
      const isShippingOnly = this.rewards.length === 1 && this.rewards[0].type === 'shipping';

      let message = '';

      if (total === 0) {
        const rewardText = this.rewards[0].type === 'shipping' ? 'FREE SHIPPING!' : 'FREE gift!';
        message = isShippingOnly
          ? `Add items to start earning <span class="spend-emphasis">${rewardText}</span>`
          : `Add items to your cart to start earning <span class="spend-emphasis">${rewardText}</span>`;
      } else if (rewardIndex >= this.rewards.length) {
        if (this.rewards.length === 1) {
          const rewardText = this.rewards[0].type === 'shipping' ? 'FREE SHIPPING!' : 'FREE gift!';
          message = `You've unlocked <span class="spend-emphasis">${rewardText}</span>`;
        } else {
          message = `You've unlocked all <span class="spend-emphasis">rewards!</span>`;
        }
      } else {
        const nextReward = this.rewards[rewardIndex];
        const remaining = nextReward.threshold - total;
        const rewardText = nextReward.type === 'shipping' ? 'FREE SHIPPING!' : 'FREE gift!';
        const progress = total / nextReward.threshold;

        if (progress >= 0.85) {
          message = `So close! Just <span class="spend-amount">${this.formatMoney(remaining, format)}</span> away from <span class="spend-emphasis">${rewardText}</span>`;
        } else {
          message = `Spend <span class="spend-amount">${this.formatMoney(remaining, format)}</span> more for <span class="spend-emphasis">${rewardText}</span>`;
        }
      }

      if (this.elements.message.innerHTML === message) return;

      if (animate) {
        this.elements.message.style.opacity = '0';
        this.trackTimeout(() => {
          if (!this.elements.message) return;
          this.elements.message.innerHTML = message;
          this.elements.message.style.opacity = '1';
        }, 400);
      } else {
        this.elements.message.innerHTML = message;
      }
    }

    updateMarkers(total) {
      const rewardIndex = this.getRewardIndex(total);

      this.rewards.forEach((reward, i) => {
        const marker = this.elements.markers[i];
        if (!marker) return;

        marker.classList.remove('spend-and-earn__marker--approaching');

        if (rewardIndex > i) {
          marker.classList.remove('spend-and-earn__marker--locked');
          marker.classList.add('spend-and-earn__marker--unlocked');
        } else {
          marker.classList.add('spend-and-earn__marker--locked');
          marker.classList.remove('spend-and-earn__marker--unlocked');

          // Pulse when approaching this reward (85–99%)
          if (rewardIndex === i) {
            const progress = total / reward.threshold;
            if (progress >= SpendAndEarn.APPROACH_THRESHOLD) {
              marker.classList.add('spend-and-earn__marker--approaching');
            }
          }
        }
      });
    }

    updateNextGiftPreview(total) {
      if (!this.elements.nextGift) return;

      const rewardIndex = this.getRewardIndex(total);

      // Reset all next gift items
      this.elements.nextGiftItems.forEach(item => {
        if (!item) return;
        item.classList.remove(
          'spend-and-earn__next-gift-item--visible',
          'spend-and-earn__next-gift-item--unlocked-visible',
          'spend-and-earn__next-gift-item--unlocking',
          'spend-and-earn__next-gift-item--entering'
        );
      });

      if (rewardIndex >= this.rewards.length) {
        this.elements.nextGift.classList.add('spend-and-earn__next-gift--hidden');
      } else {
        this.elements.nextGift.classList.remove('spend-and-earn__next-gift--hidden');
        const nextItem = this.elements.nextGiftItems[rewardIndex];
        if (nextItem) {
          nextItem.classList.add('spend-and-earn__next-gift-item--visible');
        }
      }
    }

    updateRewardsList(total) {
      const rewardIndex = this.getRewardIndex(total);

      this.rewards.forEach((reward, i) => {
        const item = this.elements.rewardItems[i];
        if (!item) return;

        if (rewardIndex > i) {
          item.classList.add('spend-and-earn__reward--unlocked', 'spend-and-earn__reward--revealed');
        } else {
          item.classList.remove('spend-and-earn__reward--unlocked', 'spend-and-earn__reward--revealed');
        }
      });
    }

    // ──────────────────────────────────────────────
    // Progress animation
    // ──────────────────────────────────────────────

    setProgressInstant(total) {
      const progress = this.calculateProgress(total);
      if (this.elements.fill) {
        this.elements.fill.style.transition = 'none';
        this.elements.fill.style.width = `${progress}%`;
        void this.elements.fill.offsetWidth;
        this.elements.fill.style.transition = '';
      }
      if (this.elements.track) {
        this.elements.track.setAttribute('aria-valuenow', Math.round(progress));
      }
      this.updateMessage(total, false);
      this.updateMarkers(total);
      this.updateNextGiftPreview(total);
      this.updateRewardsList(total);
    }

    async animateTo(total) {
      if (!this.transitionTo(SpendAndEarn.STATES.ANIMATING)) return;

      const signal = this.createAbortController();

      try {
        const targetProgress = this.calculateProgress(total);
        const currentProgress = parseFloat(this.elements.fill?.style.width) || 0;

        if (this.elements.fill && currentProgress !== targetProgress) {
          if (targetProgress > currentProgress) {
            this.spawnGoldParticles(6);
            this.triggerFillAnimation();
            this.vibrate('progress');
          }

          await this.animateElement(
            this.elements.fill, 'width',
            `${currentProgress}%`, `${targetProgress}%`,
            SpendAndEarn.TIMING.FILL_DURATION
          );
        }

        this.updateUI(total);
      } catch (error) {
        if (error.name !== 'AbortError') this.warn('Animation error', error);
      } finally {
        if (this.state === SpendAndEarn.STATES.ANIMATING) {
          this.state = SpendAndEarn.STATES.IDLE;
        }
      }
    }

    // ──────────────────────────────────────────────
    // Celebration
    // ──────────────────────────────────────────────

    async celebrateReward(rewardIndex, signal) {
      const marker = this.elements.markers[rewardIndex];

      if (marker) {
        marker.classList.remove('spend-and-earn__marker--locked', 'spend-and-earn__marker--approaching');
        marker.classList.add('spend-and-earn__marker--unlocked', 'spend-and-earn__marker--celebrating');

        this.trackTimeout(() => {
          if (marker) marker.classList.remove('spend-and-earn__marker--celebrating');
        }, SpendAndEarn.TIMING.MARKER_CELEBRATE);
      }

      this.vibrate('unlock');
      this.spawnGoldParticles(12);
      this.transitionTo(SpendAndEarn.STATES.CELEBRATING);
      this.triggerConfetti(rewardIndex);

      await this.delay(SpendAndEarn.TIMING.CELEBRATION_PAUSE, signal);
    }

    triggerConfetti(rewardIndex = 0) {
      if (!this.elements.confetti) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const marker = this.elements.markers[rewardIndex];
      if (!marker) return;

      const markerIcon = marker.querySelector('.spend-and-earn__marker-icon');
      const originRect = markerIcon ? markerIcon.getBoundingClientRect() : marker.getBoundingClientRect();

      const colors = [
        '#936347', '#B8860B', '#D4AF37', '#F4D03F',
        '#f3f2ec', '#E5DED5', '#C9A86C', '#8B5A3C'
      ];

      const isFinalReward = rewardIndex === this.rewards.length - 1 && this.rewards.length > 1;
      const confettiCount = isFinalReward ? 60 : 40;
      const explosionRadius = isFinalReward ? 250 : 180;
      const baseDuration = isFinalReward ? 1600 : 1200;

      const confettiRect = this.elements.confetti.getBoundingClientRect();
      const startX = originRect.left - confettiRect.left + (originRect.width / 2);
      const startY = originRect.top - confettiRect.top + (originRect.height / 2);

      for (let i = 0; i < confettiCount; i++) {
        const piece = document.createElement('div');
        piece.className = 'confetti-piece';

        piece.style.background = colors[Math.floor(Math.random() * colors.length)];
        piece.style.left = `${startX}px`;
        piece.style.top = `${startY}px`;

        const size = 6 + Math.random() * 8;
        piece.style.width = `${size}px`;
        piece.style.height = `${size}px`;
        piece.style.borderRadius = Math.random() > 0.5 ? '50%' : '2px';

        this.elements.confetti.appendChild(piece);

        const angle = Math.random() * 360 * (Math.PI / 180);
        const distance = Math.random() * explosionRadius;
        const randomX = Math.cos(angle) * distance;
        const randomY = Math.sin(angle) * distance;
        const rotation = 360 + Math.random() * 720;
        const duration = baseDuration + Math.random() * 600;
        const delay = Math.random() * 100;

        const animation = piece.animate([
          { transform: 'translate(0, 0) rotate(0deg) scale(1)', opacity: 1 },
          { transform: `translate(${randomX}px, ${randomY}px) rotate(${rotation}deg) scale(0.2)`, opacity: 0 }
        ], {
          duration, delay,
          easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          fill: 'forwards'
        });

        animation.onfinish = () => piece.remove();
      }
    }

    spawnGoldParticles(count = 8) {
      if (!this.elements.particles) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      for (let i = 0; i < count; i++) {
        const particle = document.createElement('div');
        particle.className = 'gold-particle';
        particle.style.left = `${Math.random() * 100}%`;
        particle.style.top = `${Math.random() * 100}%`;

        this.elements.particles.appendChild(particle);

        const animation = particle.animate([
          { opacity: 0, transform: 'scale(0) translateY(0)' },
          { opacity: 1, transform: 'scale(1.5) translateY(-5px)', offset: 0.3 },
          { opacity: 0.8, transform: 'scale(1) translateY(-10px)', offset: 0.7 },
          { opacity: 0, transform: 'scale(0.5) translateY(-20px)' }
        ], {
          duration: 800 + Math.random() * 400,
          delay: Math.random() * 200,
          easing: 'ease-out'
        });

        animation.onfinish = () => particle.remove();
      }
    }

    // ──────────────────────────────────────────────
    // First reward reveal (SINGLE → FULL transition)
    // ──────────────────────────────────────────────

    async animateToFirstRewardWithReveal(total) {
      if (!this.transitionTo(SpendAndEarn.STATES.ANIMATING)) return;

      const signal = this.createAbortController();

      try {
        const currentIndex = this.getRewardIndex(total);
        const hasMultipleRewards = this.rewards.length > 1;

        // PHASE 1: Fill to 100% (SINGLE scale = first reward threshold)
        await this.animateProgressFill(signal);

        // PHASE 2: Celebrate first reward
        await this.celebrateReward(0, signal);

        if (hasMultipleRewards) {
          // PHASE 3 + 4: List handoff + Scale transition (parallel)
          await Promise.all([
            this.animateRewardHandoff(0, signal),
            this.animateScaleTransition(total, signal)
          ]);

          // Handle additional rewards that are already unlocked
          for (let i = 1; i < currentIndex; i++) {
            await this.celebrateReward(i, signal);
            await this.animateRewardHandoff(i, signal);
          }
        }

        this.updateMessage(total);
        this.updateMarkers(total);

      } catch (error) {
        if (error.name !== 'AbortError') this.warn('Animation error', error);
      } finally {
        this.state = SpendAndEarn.STATES.IDLE;
      }
    }

    async animateProgressFill(signal) {
      const currentProgress = parseFloat(this.elements.fill?.style.width) || 0;

      if (this.elements.fill && currentProgress < 100) {
        this.spawnGoldParticles(10);
        this.triggerFillAnimation(6000);

        await this.animateElement(
          this.elements.fill, 'width',
          `${currentProgress}%`, '100%',
          SpendAndEarn.TIMING.FILL_DURATION
        );
      }
    }

    /** Position all markers at their threshold-relative positions for FULL scale mode */
    positionMarkersForFullScale() {
      this.rewards.forEach((reward, i) => {
        const marker = this.elements.markers[i];
        if (!marker) return;
        marker.style.left = `${(reward.threshold / this.maxThreshold) * 100}%`;
      });
    }

    async animateScaleTransition(total, signal) {
      await this.delay(SpendAndEarn.TIMING.SCALE_DELAY, signal);

      const firstMarker = this.elements.markers[0];
      this.transitionTo(SpendAndEarn.STATES.TRANSITIONING);

      // Slide first marker from right edge (100%) to its proportional position
      if (firstMarker && this.elements.track) {
        const trackRect = this.elements.track.getBoundingClientRect();
        const markerRect = firstMarker.getBoundingClientRect();

        const startX = markerRect.left - trackRect.left;
        const endX = (this.firstRewardPosition / 100) * trackRect.width;

        const slideAnimation = firstMarker.animate([
          { left: `${startX}px`, right: 'auto', transform: 'translate(-50%, -50%)' },
          { left: `${endX}px`, right: 'auto', transform: 'translate(-50%, -50%)' }
        ], {
          duration: SpendAndEarn.TIMING.SCALE_TRANSITION,
          easing: SpendAndEarn.TIMING.EASING,
          fill: 'forwards'
        });

        // Rescale progress bar simultaneously
        if (this.elements.fill) {
          this.elements.fill.style.transition = `width ${SpendAndEarn.TIMING.SCALE_TRANSITION}ms ${SpendAndEarn.TIMING.EASING}`;
          this.elements.fill.style.width = `${this.firstRewardPosition}%`;
        }

        await slideAnimation.finished;
        slideAnimation.cancel();
        if (this.elements.fill) this.elements.fill.style.transition = '';
      }

      // Hide non-first markers before switching to FULL mode
      for (let i = 1; i < this.elements.markers.length; i++) {
        if (this.elements.markers[i]) this.elements.markers[i].style.opacity = '0';
      }

      // Switch to FULL scale and position all markers
      this.setScaleMode(SpendAndEarn.SCALE_MODES.FULL);
      this.positionMarkersForFullScale();

      // Crossfade message
      if (this.elements.message) {
        this.elements.message.style.transition = 'opacity 0.3s ease';
        this.elements.message.style.opacity = '0';
      }
      await this.delay(300, signal);
      if (this.elements.message) {
        this.updateMessage(total, false);
        this.elements.message.style.opacity = '1';
      }

      // Animate progress to current FULL-scale value
      const fullProgress = this.calculateProgress(total);
      if (fullProgress > this.firstRewardPosition && this.elements.fill) {
        this.elements.fill.style.width = `${this.firstRewardPosition}%`;
        void this.elements.fill.offsetWidth;

        this.elements.fill.style.transition = `width ${SpendAndEarn.TIMING.FILL_DURATION_SLOW}ms ${SpendAndEarn.TIMING.EASE_OUT_SLOW}`;
        this.elements.fill.style.width = `${fullProgress}%`;

        await this.delay(900, signal);

        // Fade in non-first markers
        for (let i = 1; i < this.elements.markers.length; i++) {
          const marker = this.elements.markers[i];
          if (marker) {
            marker.style.transition = 'opacity 0.5s ease-out';
            marker.style.opacity = '1';
          }
        }

        await this.delay(SpendAndEarn.TIMING.FILL_DURATION_SLOW - 900, signal);

        this.elements.fill.style.transition = '';
        for (let i = 1; i < this.elements.markers.length; i++) {
          if (this.elements.markers[i]) this.elements.markers[i].style.transition = '';
        }
      } else {
        // Just fade in non-first markers
        for (let i = 1; i < this.elements.markers.length; i++) {
          const marker = this.elements.markers[i];
          if (marker) {
            marker.style.transition = 'opacity 0.5s ease-out';
            marker.style.opacity = '1';
          }
        }
        await this.delay(500, signal);
        for (let i = 1; i < this.elements.markers.length; i++) {
          if (this.elements.markers[i]) this.elements.markers[i].style.transition = '';
        }
      }
    }

    async transitionToSingleScale(total) {
      if (!this.transitionTo(SpendAndEarn.STATES.TRANSITIONING)) return;

      const signal = this.createAbortController();

      try {
        const currentProgress = parseFloat(this.elements.fill?.style.width) || 0;

        if (this.elements.fill && currentProgress > this.firstRewardPosition) {
          await this.animateElement(
            this.elements.fill, 'width',
            `${currentProgress}%`, `${this.firstRewardPosition}%`,
            SpendAndEarn.TIMING.FILL_DURATION
          );
        }

        this.setScaleMode(SpendAndEarn.SCALE_MODES.SINGLE);

        // Remove inline positioning from FULL mode
        this.elements.markers.forEach(marker => {
          if (marker) marker.style.left = '';
        });

        const singleProgress = this.calculateProgress(total);
        if (this.elements.fill) {
          this.elements.fill.style.width = '100%';
          await this.animateElement(
            this.elements.fill, 'width',
            '100%', `${singleProgress}%`,
            SpendAndEarn.TIMING.FILL_DURATION
          );
        }

        this.updateUI(total);

      } catch (error) {
        if (error.name !== 'AbortError') this.warn('Animation error', error);
      } finally {
        this.state = SpendAndEarn.STATES.IDLE;
      }
    }

    // ──────────────────────────────────────────────
    // Reward handoff animations
    // ──────────────────────────────────────────────

    /**
     * Celebrate a subsequent reward unlock (in FULL scale mode).
     * Fills to actual progress, then sequentially celebrates each newly unlocked reward.
     *
     * @param {number} total - New cart total
     * @param {number} prevRewardIndex - Number of previously unlocked rewards
     */
    async animateToWithCelebration(total, prevRewardIndex) {
      if (!this.transitionTo(SpendAndEarn.STATES.ANIMATING)) return;

      const signal = this.createAbortController();

      try {
        const targetProgress = this.calculateProgress(total);
        const currentProgress = parseFloat(this.elements.fill?.style.width) || 0;

        if (this.elements.fill && currentProgress < targetProgress) {
          this.spawnGoldParticles(10);
          this.triggerFillAnimation();

          await this.animateElement(
            this.elements.fill, 'width',
            `${currentProgress}%`, `${targetProgress}%`,
            SpendAndEarn.TIMING.FILL_DURATION
          );
        }

        const currentIndex = this.getRewardIndex(total);

        // Celebrate each newly unlocked reward sequentially
        for (let i = prevRewardIndex; i < currentIndex; i++) {
          await this.celebrateReward(i, signal);
          await this.animateRewardHandoff(i, signal);
        }

        this.updateMessage(total);
        this.updateMarkers(total);

      } catch (error) {
        if (error.name !== 'AbortError') this.warn('Animation error', error);
      } finally {
        this.state = SpendAndEarn.STATES.IDLE;
      }
    }

    /**
     * Animate the handoff from "next gift" preview to "unlocked reward" list.
     * Shows a check on the locked item, exits it, enters it in the rewards list,
     * and optionally reveals the next locked reward.
     */
    async animateRewardHandoff(rewardIdx, signal) {
      const isLastReward = rewardIdx >= this.rewards.length - 1;
      const nextIdx = rewardIdx + 1;
      const hasNextReward = nextIdx < this.rewards.length;

      await this._performUnlockAnimation({
        lockedElement: this.elements.nextGiftItems[rewardIdx],
        rewardElement: this.elements.rewardItems[rewardIdx],
        initialDelay: rewardIdx === 0 ? 200 : 400,
        collapseContainer: isLastReward,
        onCleanup: async (lockedElement, rewardElement, sig) => {
          // Clean up the exited locked item
          if (lockedElement) {
            lockedElement.classList.remove(
              'spend-and-earn__next-gift-item--visible',
              'spend-and-earn__next-gift-item--unlocked-visible',
              'spend-and-earn__next-gift-item--unlocking',
              'spend-and-earn__next-gift-item--entering'
            );
          }

          // Reveal the next locked reward (if any)
          if (hasNextReward) {
            await this.delay(SpendAndEarn.TIMING.NEXT_LOCKED_ENTER_DELAY, sig);

            const nextItem = this.elements.nextGiftItems[nextIdx];
            if (nextItem) {
              nextItem.classList.add(
                'spend-and-earn__next-gift-item--visible',
                'spend-and-earn__next-gift-item--entering'
              );
              await this.delay(SpendAndEarn.TIMING.NEXT_LOCKED_ENTER_DURATION + 100, sig);
              nextItem.classList.remove('spend-and-earn__next-gift-item--entering');
            }
          }
        }
      }, signal);
    }

    /**
     * Core unlock animation shared by all reward handoff sequences.
     *
     * @param {Object} config
     * @param {HTMLElement} config.lockedElement - Next-gift item being unlocked
     * @param {HTMLElement} config.rewardElement - Reward list item being revealed
     * @param {number} config.initialDelay - Breathing room before animation
     * @param {boolean} config.collapseContainer - Collapse next-gift container (final reward)
     * @param {Function} config.onCleanup - Async callback after exit completes
     * @param {AbortSignal} signal
     */
    async _performUnlockAnimation(config, signal) {
      const { lockedElement, rewardElement, initialDelay, collapseContainer, onCleanup } = config;

      // Phase 1: Breathing room after celebration
      await this.delay(initialDelay, signal);

      // Phase 2: Show check — "You unlocked it!"
      if (lockedElement) {
        lockedElement.classList.add('spend-and-earn__next-gift-item--unlocked-visible');
        this.vibrate('check');
      }

      // Phase 3: Hold unlocked state
      await this.delay(SpendAndEarn.TIMING.CHECK_POP + SpendAndEarn.TIMING.CHECK_HOLD, signal);

      // Phase 4: Start exit animation
      if (lockedElement) {
        lockedElement.classList.add('spend-and-earn__next-gift-item--unlocking');
      }

      // Collapse container if this is the final reward
      if (collapseContainer && this.elements.nextGift) {
        this.elements.nextGift.classList.add('spend-and-earn__next-gift--hidden');
      }

      // Phase 5: Start reward entering (overlapped with exit)
      await this.delay(SpendAndEarn.TIMING.LOCKED_EXIT_START_DELAY, signal);
      if (rewardElement) {
        rewardElement.classList.add('spend-and-earn__reward--unlocked', 'spend-and-earn__reward--entering');
      }

      // Phase 6: Wait for exit to complete
      await this.delay(SpendAndEarn.TIMING.LOCKED_EXIT, signal);

      // Phase 7: Custom cleanup
      if (onCleanup) {
        await onCleanup(lockedElement, rewardElement, signal);
      }

      // Phase 8: Wait for reward to settle
      const settleTime = SpendAndEarn.TIMING.UNLOCKED_ENTER -
        (SpendAndEarn.TIMING.LOCKED_EXIT_START_DELAY + SpendAndEarn.TIMING.LOCKED_EXIT);
      if (settleTime > 0) {
        await this.delay(settleTime, signal);
      }

      // Phase 9: Finalize reward
      if (rewardElement) {
        rewardElement.classList.add('spend-and-earn__reward--revealed');
        rewardElement.classList.remove('spend-and-earn__reward--entering');
      }
    }

    // ──────────────────────────────────────────────
    // Cart update handling + initialization
    // ──────────────────────────────────────────────

    async initialize() {
      if (this.rewards.length === 0) return;

      const currentTotal = parseInt(this.dataset.cartTotal, 10) || 0;
      const previousState = this.loadSessionState();
      const currentIndex = this.getRewardIndex(currentTotal);

      // Set initial scale mode
      if (this.rewards.length > 1 && currentIndex >= 1) {
        this.setScaleMode(SpendAndEarn.SCALE_MODES.FULL);
        this.positionMarkersForFullScale();
      } else {
        this.setScaleMode(SpendAndEarn.SCALE_MODES.SINGLE);
      }

      const shouldCelebrateFirst = previousState &&
        previousState.rewardIndex === 0 &&
        currentIndex >= 1 &&
        previousState.scaleMode === SpendAndEarn.SCALE_MODES.SINGLE;

      const shouldCelebrateSubsequent = this.rewards.length > 1 &&
        previousState &&
        previousState.rewardIndex >= 1 &&
        currentIndex > previousState.rewardIndex;

      this.saveSessionState(currentTotal, currentIndex);

      if (shouldCelebrateFirst) {
        this.setScaleMode(SpendAndEarn.SCALE_MODES.SINGLE);
        this.setProgressInstant(previousState.total);
        this.updateMessage(previousState.total);
        await this.animateToFirstRewardWithReveal(currentTotal);
      } else if (shouldCelebrateSubsequent) {
        this.setProgressInstant(previousState.total);
        this.updateMessage(previousState.total);
        await this.animateToWithCelebration(currentTotal, previousState.rewardIndex);
      } else if (previousState && previousState.total !== currentTotal) {
        this.setProgressInstant(previousState.total);
        await this.animateTo(currentTotal);
      } else {
        this.setProgressInstant(currentTotal);
        this.updateMessage(currentTotal, false);
        this.updateMarkers(currentTotal);
        this.updateRewardsList(currentTotal);
      }
    }

    handleCartUpdateStart() {
      SpendAndEarn.isCartUpdating = true;
      SpendAndEarn.cartUpdateTimestamp = Date.now();
    }

    handleCartUpdateError() {
      SpendAndEarn.isCartUpdating = false;
    }

    /** Filter out DESTASH and ADVENT items from cart total */
    filterEligibleTotal(cart) {
      if (!cart?.items) return cart?.total_price || 0;
      return cart.items.reduce((sum, item) => {
        const sku = (item.sku || '').toUpperCase();
        if (sku.includes('DESTASH') || sku.includes('ADVENT')) return sum;
        return sum + (item.final_line_price || 0);
      }, 0);
    }

    async onCartUpdate(event) {
      const cart = event.detail?.cart || {};
      const newTotal = this.filterEligibleTotal(cart);
      const previousTotal = parseInt(this.dataset.cartTotal, 10) || 0;

      // Skip if component was just recreated (initialize() already handled it)
      const timeSinceCartUpdate = Date.now() - SpendAndEarn.cartUpdateTimestamp;
      const wasJustRecreated = SpendAndEarn.isCartUpdating || timeSinceCartUpdate < 500;

      if (wasJustRecreated) {
        SpendAndEarn.isCartUpdating = false;
        return;
      }

      if (newTotal === previousTotal) return;

      const prevIndex = this.getRewardIndex(previousTotal);
      const currIndex = this.getRewardIndex(newTotal);

      this.dataset.cartTotal = newTotal;
      this.saveSessionState(newTotal, currIndex);

      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }

      const isRewardUnlock = currIndex > prevIndex;
      const isScaleReset = currIndex === 0 && this.scaleMode === SpendAndEarn.SCALE_MODES.FULL;

      if (isRewardUnlock || isScaleReset) {
        this.cancelAnimations();

        if (prevIndex === 0 && currIndex >= 1 && this.scaleMode === SpendAndEarn.SCALE_MODES.SINGLE) {
          await this.animateToFirstRewardWithReveal(newTotal);
        } else if (isRewardUnlock) {
          await this.animateToWithCelebration(newTotal, prevIndex);
        } else if (isScaleReset) {
          await this.transitionToSingleScale(newTotal);
        }

        SpendAndEarn.isCartUpdating = false;
      } else {
        // Debounce regular progress updates
        this.debounceTimer = setTimeout(async () => {
          this.cancelAnimations();
          await this.animateTo(newTotal);
          this.debounceTimer = null;
          SpendAndEarn.isCartUpdating = false;
        }, 400);
      }
    }

    // ──────────────────────────────────────────────
    // Utility
    // ──────────────────────────────────────────────

    log(...args) {
      if (SpendAndEarn.DEBUG) console.log('[SpendAndEarn]', ...args);
    }

    warn(...args) {
      if (SpendAndEarn.DEBUG) console.warn('[SpendAndEarn]', ...args);
    }

    trackTimeout(fn, ms) {
      const id = setTimeout(() => {
        this.timeouts = this.timeouts.filter(t => t !== id);
        fn();
      }, ms);
      this.timeouts.push(id);
      return id;
    }

    clearAllTimeouts() {
      this.timeouts.forEach(id => clearTimeout(id));
      this.timeouts = [];
    }

    cancelAnimations() {
      if (this.animationAbortController) {
        this.animationAbortController.abort();
        this.animationAbortController = null;
      }
      this.state = SpendAndEarn.STATES.IDLE;
    }

    createAbortController() {
      this.cancelAnimations();
      this.animationAbortController = new AbortController();
      return this.animationAbortController.signal;
    }

    delay(ms, signal) {
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(resolve, ms);
        if (signal) {
          signal.addEventListener('abort', () => {
            clearTimeout(timeout);
            reject(new DOMException('Aborted', 'AbortError'));
          });
        }
      });
    }

    async animateElement(element, property, fromValue, toValue, duration, easing = SpendAndEarn.TIMING.EASING) {
      if (!element) return;

      const originalTransition = element.style.transition;
      element.style.transition = 'none';
      element.style[property] = fromValue;
      void element.offsetWidth;

      const animation = element.animate(
        [{ [property]: fromValue }, { [property]: toValue }],
        { duration, easing, fill: 'forwards' }
      );

      try {
        await animation.finished;
        element.style[property] = toValue;
      } catch (error) {
        if (error.name !== 'AbortError') throw error;
      } finally {
        animation.cancel();
        element.style.transition = originalTransition;
      }
    }

    triggerFillAnimation(duration = 3000) {
      if (!this.elements.fill) return;

      this.elements.fill.classList.remove('spend-and-earn__fill--animating', 'spend-and-earn__fill--animating-slow');
      void this.elements.fill.offsetWidth;

      const animClass = duration > 3000 ? 'spend-and-earn__fill--animating-slow' : 'spend-and-earn__fill--animating';
      this.elements.fill.classList.add(animClass);

      this.trackTimeout(() => {
        if (!this.elements.fill) return;
        this.elements.fill.classList.remove('spend-and-earn__fill--animating', 'spend-and-earn__fill--animating-slow');
      }, duration);
    }

    vibrate(pattern) {
      if (!SpendAndEarn.ENABLE_HAPTICS || !('vibrate' in navigator)) return;

      try {
        switch (pattern) {
          case 'unlock': navigator.vibrate([50, 30, 50]); break;
          case 'check': navigator.vibrate(30); break;
          case 'progress': navigator.vibrate(10); break;
        }
      } catch (e) {
        // Vibration not supported
      }
    }

    attachEventListeners() {
      document.addEventListener('line-item:change:start', this.handleCartUpdateStart);
      document.addEventListener('line-item:change:end', this.handleCartUpdate);
      document.addEventListener('line-item:change:error', this.handleCartUpdateError);
    }

    detachEventListeners() {
      document.removeEventListener('line-item:change:start', this.handleCartUpdateStart);
      document.removeEventListener('line-item:change:end', this.handleCartUpdate);
      document.removeEventListener('line-item:change:error', this.handleCartUpdateError);
    }

    attachMicroInteractions() {
      // Hover effects on locked gifts
      this.elements.nextGiftItems.filter(Boolean).forEach(gift => {
        gift.addEventListener('mouseenter', () => {
          const image = gift.querySelector('.spend-and-earn__next-gift-image');
          if (image && !gift.classList.contains('spend-and-earn__next-gift-item--unlocked-visible')) {
            image.style.transition = 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease, filter 0.3s ease';
            image.style.transform = 'scale(1.05)';
          }
        });

        gift.addEventListener('mouseleave', () => {
          const image = gift.querySelector('.spend-and-earn__next-gift-image');
          if (image) image.style.transform = 'scale(1)';
        });
      });

      // Progress bar hover shimmer
      if (this.elements.track) {
        let isAnimating = false;

        this.elements.track.addEventListener('mouseenter', () => {
          if (isAnimating) return;
          isAnimating = true;
          this.triggerFillAnimation(3000);
          this.trackTimeout(() => { isAnimating = false; }, 3000);
        });
      }
    }

    formatMoney(cents, format) {
      if (typeof window.formatMoney === 'function') {
        try {
          const formatted = window.formatMoney(cents, format);
          const temp = document.createElement('div');
          temp.innerHTML = formatted;
          return temp.textContent || temp.innerText || formatted;
        } catch {
          // Fall through to manual formatting
        }
      }

      if (typeof cents === 'string') cents = parseFloat(cents);
      if (isNaN(cents)) cents = 0;

      const value = (cents / 100).toFixed(2);
      const valueNoDecimals = Math.floor(cents / 100);
      const valueWithComma = value.replace('.', ',');
      const valueWithApostrophe = value.replace('.', "'");

      return format
        .replace('{{amount_with_comma_separator}}', valueWithComma)
        .replace('{{amount_no_decimals_with_comma_separator}}', valueNoDecimals.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','))
        .replace('{{amount_no_decimals}}', valueNoDecimals.toString())
        .replace('{{amount_with_apostrophe_separator}}', valueWithApostrophe)
        .replace('{{amount}}', value);
    }
  }

  customElements.define('spend-and-earn', SpendAndEarn);
}
